import type { AdminVehicleCatalogMakeListItemDto } from '@carcheck/shared';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogFilterBar } from './_components/CatalogFilterBar';
import { CreateMakeDialog } from './_components/CreateMakeDialog';
import { buildCatalogColumns } from './_lib/catalogColumns';
import {
  catalogFiltersSchema,
  filterKeyOf,
  hasActiveFilters,
  toListParams,
} from './_lib/catalogFilters';
import { useMakeRoute } from './_lib/useMakeRoute';
import { MakeDrawer } from './MakeDrawer';
import { useCatalogMakes } from '../../api/admin-vehicle-catalog';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/vehicle-catalog` — «Справочник авто» (AUT-437 / VC-06), after
 * `design/mockups/admin/vehicle-catalog.html`. Catalogue editor only: the
 * suggestion queue is switched off by the product owner.
 *
 * Gated on `moderate_reports` (the API's class-level permission) around the
 * component that OWNS the query — an admin without it sends nothing. Makes
 * list: server filters in the URL, cursor pages bridged by
 * `useCursorPagination`. A row (or `/vehicle-catalog/:makeId`) opens the
 * make drawer; «Добавить марку» creates one and opens its drawer at once.
 * The pager resets after a create or a rename (the list is sorted by name).
 *
 * Not here: the header totals / catalogue version and the «Без видимых
 * моделей» filter (no server support).
 */
const PageHeader = ({ onCreate }: { onCreate: () => void }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('vehicleCatalog.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('vehicleCatalog.subtitle')}
        </Typography>
      </Box>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
        {t('vehicleCatalog.create')}
      </Button>
    </Box>
  );
};

const VehicleCatalogPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(catalogFiltersSchema);
  const pager = useCursorPagination();
  const { makeId, openMake, closeMake } = useMakeRoute();
  const [creating, setCreating] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const filterKey = filterKeyOf(filters);
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable; re-run only when the filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useCatalogMakes(toListParams(filters, pager.cursor, pager.pageSize));
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildCatalogColumns(t), [t]);

  const onCreated = (id: string): void => {
    setCreating(false);
    setCreatedId(id);
    pager.reset();
    openMake(id);
  };
  const openFromDialog = (id: string): void => {
    setCreating(false);
    openMake(id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <PageHeader onCreate={() => setCreating(true)} />

      <CatalogFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminVehicleCatalogMakeListItemDto>
        rows={items}
        columns={columns}
        getRowId={(row) => row.id}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => openMake(row.id)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {filters.q
              ? t('vehicleCatalog.emptySearch', { q: filters.q })
              : t('vehicleCatalog.empty')}
          </Typography>
        }
      />

      <CreateMakeDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={onCreated}
        onOpenMake={openFromDialog}
      />
      <MakeDrawer
        makeId={makeId}
        justCreated={makeId !== null && makeId === createdId}
        onClose={closeMake}
        onOpenMake={openMake}
        onRenamed={pager.reset}
      />
    </Box>
  );
};

/** Whole-page gate: no `moderate_reports` → the default 403 panel, no requests. */
export const VehicleCatalogPage = (): ReactElement => (
  <RequirePermission perm="moderate_reports">
    <VehicleCatalogPageInner />
  </RequirePermission>
);
